/**
 * Chatbot Mutations Service
 * 
 * Centralized service for all chatbot mutation operations.
 * Provides a single source of truth for all Supabase mutations related to chatbots.
 */

import { supabase } from '@/integrations/supabase/client';
import { validateChatbotId } from '@/utils/chatbotUtils';
import { prepareThemeForDatabase } from '@/utils/themeUtils';
import { Question, TriggerRule } from '@/types/theme';
import { ChatTheme } from '@/types/theme';
import { v4 as uuidv4 } from 'uuid';

/**
 * Updates chatbot data
 */
export async function updateChatbotData(
  chatbotId: string,
  updates: Record<string, any>
): Promise<any> {
  const validId = validateChatbotId(chatbotId);
  if (!validId) {
    throw new Error('Invalid chatbot ID format');
  }

  const { data, error } = await supabase
    .from('chatbots')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', validId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Deletes a chatbot
 */
export async function deleteChatbot(chatbotId: string): Promise<void> {
  const validId = validateChatbotId(chatbotId);
  if (!validId) {
    throw new Error('Invalid chatbot ID format');
  }

  const { error } = await supabase
    .from('chatbots')
    .delete()
    .eq('id', validId);

  if (error) throw error;
}

/**
 * Uploads chatbot avatar
 */
export async function uploadChatbotAvatar(
  chatbotId: string,
  file: File
): Promise<string> {
  const validId = validateChatbotId(chatbotId);
  if (!validId) {
    throw new Error('Invalid chatbot ID format');
  }

  // Check if file is empty (for removal)
  if (file.size === 0) {
    await updateChatbotData(validId, { avatar_url: null });
    return '';
  }

  // Generate unique filename
  const fileExt = file.name.split('.').pop();
  const fileName = `${validId}/${uuidv4()}.${fileExt}`;
  const filePath = fileName;

  // Upload file
  const { error: uploadError } = await supabase
    .storage
    .from('chatbot-avatars')
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: true
    });

  if (uploadError) throw uploadError;

  // Get public URL
  const { data } = supabase
    .storage
    .from('chatbot-avatars')
    .getPublicUrl(filePath);

  // Update chatbot record
  await updateChatbotData(validId, { avatar_url: data.publicUrl });

  return data.publicUrl;
}

/**
 * Uploads operator avatar
 */
export async function uploadOperatorAvatar(
  chatbotId: string,
  file: File
): Promise<string> {
  const validId = validateChatbotId(chatbotId);
  if (!validId) {
    throw new Error('Invalid chatbot ID format');
  }

  // Check if file is empty (for removal)
  if (file.size === 0) {
    await updateChatbotData(validId, { operator_avatar_url: null });
    return '';
  }

  // Generate unique filename
  const fileExt = file.name.split('.').pop();
  const fileName = `${validId}/operator-${uuidv4()}.${fileExt}`;
  const filePath = fileName;

  // Upload file
  const { error: uploadError } = await supabase
    .storage
    .from('chatbot-avatars')
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: true
    });

  if (uploadError) throw uploadError;

  // Get public URL
  const { data } = supabase
    .storage
    .from('chatbot-avatars')
    .getPublicUrl(filePath);

  if (!data.publicUrl) {
    throw new Error('Failed to get public URL for uploaded file');
  }

  // Update chatbot record
  await updateChatbotData(validId, { operator_avatar_url: data.publicUrl });

  return data.publicUrl;
}

/**
 * Uploads company logo
 */
export async function uploadCompanyLogo(
  chatbotId: string,
  file: File
): Promise<string> {
  const validId = validateChatbotId(chatbotId);
  if (!validId) {
    throw new Error('Invalid chatbot ID format');
  }

  // Check if file is empty (for removal)
  if (file.size === 0) {
    await updateChatbotData(validId, { company_logo: null });
    return '';
  }

  // Generate unique filename
  const fileExt = file.name.split('.').pop();
  const fileName = `${validId}/company-${uuidv4()}.${fileExt}`;
  const filePath = fileName;

  // Upload file
  const { error: uploadError } = await supabase
    .storage
    .from('chatbot-avatars')
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: true
    });

  if (uploadError) throw uploadError;

  // Get public URL
  const { data } = supabase
    .storage
    .from('chatbot-avatars')
    .getPublicUrl(filePath);

  if (!data.publicUrl) {
    throw new Error('Failed to get public URL for uploaded file');
  }

  // Update chatbot record
  await updateChatbotData(validId, { company_logo: data.publicUrl });

  return data.publicUrl;
}

/**
 * Updates theme
 */
export async function updateTheme(
  chatbotId: string,
  theme: Partial<ChatTheme>
): Promise<any> {
  const validId = validateChatbotId(chatbotId);
  if (!validId) {
    throw new Error('Invalid chatbot ID format');
  }

  const dbUpdates = prepareThemeForDatabase(theme);

  const { data, error } = await supabase
    .from('themes')
    .update(dbUpdates)
    .eq('chatbot_id', validId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Upserts theme (creates if doesn't exist)
 */
export async function upsertTheme(
  chatbotId: string,
  theme: Partial<ChatTheme>
): Promise<any> {
  const validId = validateChatbotId(chatbotId);
  if (!validId) {
    throw new Error('Invalid chatbot ID format');
  }

  const dbTheme = prepareThemeForDatabase(theme);

  const { data, error } = await supabase
    .from('themes')
    .upsert({
      chatbot_id: validId,
      ...dbTheme,
      updated_at: new Date().toISOString()
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Adds a question
 */
export async function addQuestion(
  chatbotId: string,
  question: Omit<Question, 'id' | 'chatbot_id'>
): Promise<any> {
  const validId = validateChatbotId(chatbotId);
  if (!validId) {
    throw new Error('Invalid chatbot ID format');
  }

  // Prepare validation settings
  let responsevalidation = question.responsevalidation;

  if (question.validationType || question.validation_rules) {
    const validationSettings = typeof question.responsevalidation === 'string'
      ? JSON.parse(question.responsevalidation || '{}')
      : (question.responsevalidation || {});

    if (question.validationType) {
      validationSettings.validationType = question.validationType;
    }

    if (question.validation_rules) {
      validationSettings.validation_rules = question.validation_rules;
    }

    responsevalidation = JSON.stringify(validationSettings);
  }

  const dbQuestion = {
    chatbot_id: validId,
    text: question.text,
    type: question.type,
    order: question.order,
    options: question.options || null,
    shortname: question.shortname || null,
    errormessage: question.errormessage || null,
    responsevalidation: responsevalidation || null,
    validationType: question.validationType || 'none',
    enable_ai_response: question.enable_ai_response || false,
    ai_response_prompt: question.ai_response_prompt || null,
    rich_content: question.rich_content || null
  };

  const { data, error } = await supabase
    .from('questions')
    .insert([dbQuestion])
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Updates a question
 */
export async function updateQuestion(
  chatbotId: string,
  questionId: string,
  updates: Partial<Question>
): Promise<any> {
  const validId = validateChatbotId(chatbotId);
  if (!validId) {
    throw new Error('Invalid chatbot ID format');
  }

  const dbUpdates: any = {};

  if (updates.text !== undefined) dbUpdates.text = updates.text;
  if (updates.type !== undefined) dbUpdates.type = updates.type;
  if (updates.order !== undefined) dbUpdates.order = updates.order;
  if (updates.options !== undefined) dbUpdates.options = updates.options;
  if (updates.shortname !== undefined) dbUpdates.shortname = updates.shortname;
  if (updates.errormessage !== undefined) dbUpdates.errormessage = updates.errormessage;
  if (updates.rich_content !== undefined) dbUpdates.rich_content = updates.rich_content;
  if (updates.enable_ai_response !== undefined) dbUpdates.enable_ai_response = updates.enable_ai_response;
  if (updates.ai_response_prompt !== undefined) dbUpdates.ai_response_prompt = updates.ai_response_prompt;

  // Handle validation settings
  if (updates.validationType !== undefined || updates.validation_rules !== undefined) {
    const validationSettings = typeof updates.responsevalidation === 'string'
      ? JSON.parse(updates.responsevalidation || '{}')
      : (updates.responsevalidation || {});

    if (updates.validationType !== undefined) {
      validationSettings.validationType = updates.validationType;
      dbUpdates.validationType = updates.validationType;
    }

    if (updates.validation_rules !== undefined) {
      validationSettings.validation_rules = updates.validation_rules;
    }

    dbUpdates.responsevalidation = JSON.stringify(validationSettings);
  } else if (updates.responsevalidation !== undefined) {
    dbUpdates.responsevalidation = updates.responsevalidation;
  }

  const { data, error } = await supabase
    .from('questions')
    .update(dbUpdates)
    .eq('id', questionId)
    .eq('chatbot_id', validId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Deletes a question
 */
export async function deleteQuestion(
  chatbotId: string,
  questionId: string
): Promise<void> {
  const validId = validateChatbotId(chatbotId);
  if (!validId) {
    throw new Error('Invalid chatbot ID format');
  }

  const { error } = await supabase
    .from('questions')
    .delete()
    .eq('id', questionId)
    .eq('chatbot_id', validId);

  if (error) throw error;
}

/**
 * Updates questions order
 */
export async function updateQuestionsOrder(
  chatbotId: string,
  questions: Question[]
): Promise<void> {
  const validId = validateChatbotId(chatbotId);
  if (!validId) {
    throw new Error('Invalid chatbot ID format');
  }

  if (!questions || !Array.isArray(questions) || questions.length === 0) {
    throw new Error('No questions provided for order update');
  }

  // Update order for each question
  const updatePromises = questions.map((question, index) => {
    return supabase
      .from('questions')
      .update({ order: index })
      .eq('id', question.id)
      .eq('chatbot_id', validId);
  });

  const results = await Promise.all(updatePromises);

  // Check for errors
  const errors = results.filter(result => result.error);
  if (errors.length > 0) {
    throw new Error(`Failed to update questions: ${errors.map(e => e.error?.message).join(', ')}`);
  }
}

/**
 * Adds a trigger rule
 */
export async function addTriggerRule(chatbotId: string): Promise<any> {
  const validId = validateChatbotId(chatbotId);
  if (!validId) {
    throw new Error('Invalid chatbot ID format');
  }

  const { data, error } = await supabase
    .from('trigger_rules')
    .insert([{
      chatbot_id: validId,
      name: 'New Trigger',
      conditions: [],
      postvariablemappings: [],
      active: true
    }])
    .select();

  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error('Failed to create trigger rule: no data returned');
  }
  return data[0];
}

/**
 * Updates a trigger rule
 */
export async function updateTriggerRule(
  chatbotId: string,
  ruleId: string,
  updates: Partial<TriggerRule>
): Promise<any> {
  const validId = validateChatbotId(chatbotId);
  if (!validId) {
    throw new Error('Invalid chatbot ID format');
  }

  const dbUpdates: Record<string, any> = {};

  if ('name' in updates) dbUpdates.name = updates.name;
  if ('notes' in updates) dbUpdates.notes = updates.notes;
  if ('active' in updates) dbUpdates.active = updates.active;
  if ('webhookUrl' in updates) dbUpdates.webhookurl = updates.webhookUrl;

  if ('conditions' in updates && Array.isArray(updates.conditions)) {
    dbUpdates.conditions = JSON.stringify(updates.conditions);
  }

  if ('postVariableMappings' in updates && Array.isArray(updates.postVariableMappings)) {
    dbUpdates.postvariablemappings = updates.postVariableMappings;
  }

  const { data, error } = await supabase
    .from('trigger_rules')
    .update(dbUpdates)
    .eq('id', ruleId)
    .eq('chatbot_id', validId)
    .select();

  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error(`Trigger rule with id ${ruleId} not found`);
  }
  return data[0];
}

/**
 * Deletes a trigger rule
 */
export async function deleteTriggerRule(
  chatbotId: string,
  ruleId: string
): Promise<void> {
  const validId = validateChatbotId(chatbotId);
  if (!validId) {
    throw new Error('Invalid chatbot ID format');
  }

  const { error } = await supabase
    .from('trigger_rules')
    .delete()
    .eq('id', ruleId)
    .eq('chatbot_id', validId);

  if (error) throw error;
}

/**
 * Saves all trigger rules for a chatbot
 * Deletes existing rules and inserts new ones
 */
export async function saveAllTriggerRules(
  chatbotId: string,
  triggerRules: TriggerRule[]
): Promise<any[]> {
  const validId = validateChatbotId(chatbotId);
  if (!validId) {
    throw new Error('Invalid chatbot ID format');
  }

  console.log('🎯 Saving trigger rules:', triggerRules);

  // Delete existing rules for this chatbot
  const { error: deleteError } = await supabase
    .from('trigger_rules')
    .delete()
    .eq('chatbot_id', validId);

  if (deleteError) {
    console.error('❌ Error deleting existing trigger rules:', deleteError);
    throw deleteError;
  }

  // Insert all rules (using provided UUIDs)
  const rulesToSave = triggerRules.map(rule => ({
    id: rule.id,
    chatbot_id: validId,
    name: rule.name,
    conditions: JSON.stringify(rule.conditions || []),
    postvariablemappings: JSON.stringify(rule.postVariableMappings || []),
    active: rule.active ?? true,
    notes: rule.notes || '',
    webhookurl: rule.webhookUrl || '',
    created_at: rule.created_at || new Date().toISOString(),
    updated_at: new Date().toISOString()
  }));

  if (rulesToSave.length > 0) {
    const { data, error } = await supabase
      .from('trigger_rules')
      .insert(rulesToSave)
      .select();

    if (error) {
      console.error('❌ Error inserting trigger rules:', error);
      throw error;
    }

    console.log('✅ Trigger rules saved successfully:', data);
    return data || [];
  }

  return [];
}



